import { applyRate, Rate } from "@redotech/fx";
import { Money, StruckSnapshot } from "@redotech/money";
import { Currency } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";

/**
 * Anchor symbol disambiguation to the store currency: the store currency reads
 * as its clean home symbol ($), and foreign currencies get a prefix (US$, CA$),
 * regardless of the viewer's browser locale. Only the symbol-ambiguous "$"
 * family needs this — €, £, ¥, etc. are unambiguous anywhere. The $-family
 * (CAD, AUD, NZD, SGD, HKD, MXN) always renders prefixed, never a bare $
 * (see {@link format}).
 */
const STORE_LOCALE: Partial<Record<Currency, string>> = {
  [Currency.USD]: "en-US",
  [Currency.CAD]: "en-CA",
  [Currency.AUD]: "en-AU",
  [Currency.NZD]: "en-NZ",
  [Currency.SGD]: "en-SG",
  [Currency.HKD]: "en-HK",
  [Currency.MXN]: "es-MX",
};

// $-family currencies that must never render as a bare "$" — it reads as USD.
// en-US prefixes them (CA$, A$, NZ$, HK$, MX$, SGD).
const ALWAYS_DISAMBIGUATE: ReadonlySet<Currency> = new Set([
  Currency.CAD,
  Currency.AUD,
  Currency.NZD,
  Currency.SGD,
  Currency.HKD,
  Currency.MXN,
]);

function format(
  amount: number,
  currency: Currency,
  locale: string | undefined,
): string {
  const symbolLocale = ALWAYS_DISAMBIGUATE.has(currency) ? "en-US" : locale;
  return moneyFormat({ amount, currency }, "symbol", symbolLocale);
}

/** Rate implied by a frozen pair: native units per 1 unit of the struck currency. */
function impliedRate(value: Money, struck: StruckSnapshot): number {
  return struck.amount === 0 ? 0 : value.amount / struck.amount;
}

export interface MoneyDisplay {
  /** Main amount; `null` while a live rate is pending. */
  primary: string | null;
  /** Counterpart amount shown subtly beside primary; `null` when there's only one figure. */
  secondary: string | null;
  /** Hover text explaining the conversion + rate; `null` otherwise. */
  tooltip: string | null;
}

/**
 * Resolve how a {@link Money} renders. Default: the store currency is the main
 * figure (converting live when needed) with the value's own currency beside it.
 * Exceptions:
 * - already the store currency -> just the amount.
 * - a STRUCK value is historical, never live-converted: its frozen snapshot's
 *   store-currency leg becomes the main figure, else the historical value shows
 *   as-is (we don't apply a live rate to a frozen amount).
 * - `historical` (caller-forced, no snapshot) -> the native amount as-is, with
 *   no live conversion and no secondary. For past figures where today's rate
 *   would misrepresent them but no frozen snapshot was captured.
 *
 * Symbols are anchored to the store currency (see {@link STORE_LOCALE}).
 */
export function resolveMoneyDisplay(
  value: Money,
  storeCurrency: Currency,
  liveRate?: Rate,
  historical = false,
): MoneyDisplay {
  const locale = STORE_LOCALE[storeCurrency];
  const native = format(value.amount, value.currency, locale);

  if (value.currency === storeCurrency) {
    return { primary: native, secondary: null, tooltip: null };
  }

  if (value.struck) {
    const original = format(value.struck.amount, value.struck.currency, locale);
    const on = value.struck.asOf
      ? ` on ${new Date(value.struck.asOf).toLocaleDateString(locale)}`
      : "";
    const tooltip = `Frozen at purchase${on}: ${original} = ${native} (rate ${impliedRate(value, value.struck).toFixed(4)})`;
    return value.struck.currency === storeCurrency
      ? // store-currency leg in the snapshot -> it's the main figure
        { primary: original, secondary: native, tooltip }
      : // no store-currency leg -> just the historical value, no live conversion
        { primary: native, secondary: original, tooltip };
  }

  if (historical) {
    // Caller marked this historical but there's no frozen snapshot and no
    // captured rate — render the native amount as-is rather than apply a
    // misleading live rate.
    return { primary: native, secondary: null, tooltip: null };
  }

  if (
    liveRate &&
    liveRate.base === value.currency &&
    liveRate.quote === storeCurrency
  ) {
    const applied = applyRate(value, liveRate);
    const primary = format(applied.amount, applied.currency, locale);
    return {
      primary,
      secondary: native,
      tooltip: `${native} = ${primary} at ${liveRate.rate.toFixed(4)}`,
    };
  }

  return { primary: null, secondary: native, tooltip: null };
}
